
import * as Sentry from '@sentry/vue';
import config from './config/sentry.config';

// vue-cli sets NODE_ENV to 'production' for `yarn build` and 'development' for `yarn serve`.
const isProduction = process.env.NODE_ENV === 'production';
const enabled = !!config.dsn && (isProduction || config.enabledInDevelopment);

// Same rule as the $root.hotelCode computed in main.js: first label of the host.
function currentHotelCode() {
    try {
        if (process.env.VUE_APP_ENV === 'development' && process.env.VUE_APP_TEST_HOTEL) {
            return process.env.VUE_APP_TEST_HOTEL;
        }
        return window.location.host.split('.')[0];
    } catch (e) {
        return '';
    }
}

function inIframe() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
}

function scrubUrl(url) {
    if (typeof url !== 'string' || url.indexOf('?') === -1) return url;

    const parts = url.split('?');
    const query = parts.slice(1).join('?').split('&').map(pair => {
        const name = pair.split('=')[0];
        return config.sensitiveQueryKeys.indexOf(name.toLowerCase()) > -1 ? name + '=[Filtered]' : pair;
    }).join('&');

    return parts[0] + '?' + query;
}

function scrubRequest(request) {
    if (!request) return;

    if (request.url) request.url = scrubUrl(request.url);

    if (typeof request.query_string === 'string') {
        request.query_string = scrubUrl('?' + request.query_string).slice(1);
    }
}

// A stable random id (not personal data) so Sentry can tell "1 guest hit this 40 times"
// apart from "40 guests hit this once".
function anonymousId() {
    try {
        let id = localStorage.getItem('sentry_uid');
        if (!id) {
            id = 'g-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
            localStorage.setItem('sentry_uid', id);
        }
        return id;
    } catch (e) {
        return undefined;
    }
}

export function initSentry(Vue, router) {
    if (!enabled) return;

    Sentry.init({
        Vue,
        dsn: config.dsn,
        release: config.release,
        debug: config.debug,
        environment: process.env.VUE_APP_ENV || (isProduction ? 'production' : 'development'),

        integrations: [
            // Page load + route change performance, named by route template (/products/:id).
            Sentry.browserTracingIntegration({ router, routeLabel: 'path' }),
        ],

        tracesSampleRate: config.tracesSampleRate,

        // Never send IPs, cookies or request bodies.
        sendDefaultPii: false,
        normalizeDepth: 5,

        ignoreErrors: config.ignoreErrors,
        denyUrls: config.denyUrls,

        initialScope: {
            user: { id: anonymousId() },
            tags: {
                hotel_code: currentHotelCode(),
                server: process.env.VUE_APP_SERVER || '',
                widget: inIframe() ? 'yes' : 'no',
            },
        },

        beforeSend(event) {
            scrubRequest(event.request);
            return event;
        },

        beforeSendTransaction(event) {
            scrubRequest(event.request);

            const data = event.contexts && event.contexts.trace && event.contexts.trace.data;
            if (data) {
                Object.keys(data).forEach(key => {
                    const name = key.replace(/^query\./, '');
                    if (key !== name && config.sensitiveQueryKeys.indexOf(name.toLowerCase()) > -1) {
                        data[key] = '[Filtered]';
                    }
                });
            }

            return event;
        },

        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.data && breadcrumb.data.url) {
                breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
            }
            if (breadcrumb.category === 'navigation' && breadcrumb.data) {
                breadcrumb.data.from = scrubUrl(breadcrumb.data.from);
                breadcrumb.data.to = scrubUrl(breadcrumb.data.to);
            }
            return breadcrumb;
        },
    });
}

// Called once the hotel is loaded so every event says which hotel/template it came from.
export function setSentryHotel(hotel) {
    if (!enabled || !hotel) return;

    Sentry.setTags({
        hotel_id: hotel.id,
        hotel_uuid: hotel.uuid,
        template: hotel.template || '',
    });

    Sentry.setContext('hotel', {
        id: hotel.id,
        uuid: hotel.uuid,
        code: currentHotelCode(),
        template: hotel.template,
        currency: hotel.currency,
    });
}

export function setSentryLanguage(lang) {
    if (!enabled) return;
    Sentry.setTag('lang', lang);
}

export { Sentry };
